<template>
  <div
    :id="playerElementId"
    ref="playerElement"
    class="youtube-player"
  />
</template>

<script setup lang="ts">
  const props = defineProps<{
    videoId: string
    autoplay?: boolean
    muted?: boolean
    loop?: boolean
    start?: number
    end?: number
    showControls?: boolean
  }>()

  const emit = defineEmits({
    ready: (player: any) => true,
    playing: () => true,
    paused: () => true,
    ended: () => true,
    error: (error: any) => true,
  })

  const playerElement = ref()
  const player = ref(null)
  const loopInterval = ref(null)

  const { onLoaded } = useScriptYouTubePlayer()
  const uid = useId()
  const playerElementId = computed(() => `youtube-player-${uid}-${props.videoId}`)

  // Custom loop function
  const startLoopCheck = () => {
    if (!props.loop || !props.end) return

    loopInterval.value = setInterval(() => {
      if (player.value) {
        const currentTime = player.value.getCurrentTime()
        if (currentTime >= props.end) {
          restartVideo()
        }
      }
    }, 500) // Check every 500ms
  }

  const stopLoopCheck = () => {
    if (loopInterval.value) {
      clearInterval(loopInterval.value)
      loopInterval.value = null
    }
  }

  const restartVideo = () => {
    if (player.value) {
      const startTime = props.start || 0
      player.value.seekTo(startTime, true)
      player.value.playVideo()
    }
  }

  onMounted(() => {
    onLoaded(async ({ YT }) => {
      const YouTube = await YT
      await new Promise<void>((resolve) => {
        if (typeof YT.Player === 'undefined') YouTube.ready(resolve)
        else resolve()
      })

      player.value = new YT.Player(playerElementId.value, {
        videoId: props.videoId,
        host: 'https://www.youtube-nocookie.com',
        playerVars: {
          autoplay: props.autoplay ? 1 : 0,
          mute: props.muted ? 1 : 0,
          controls: props.showControls ? 1 : 0,
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
          start: props.start,
          end: props.end,
        },
        events: {
          onReady: (event) => emit('ready', event.target),
          onStateChange: (event) => {
            switch (event.data) {
              case 1: // Playing
                emit('playing')
                if (props.loop && props.end) startLoopCheck()
                break
              case 2: // Paused
                emit('paused')
                stopLoopCheck()
                break
              case 0: // Ended
                emit('ended')
                stopLoopCheck()
                if (props.loop) restartVideo()
                break
            }
          },
          onError: (event) => emit('error', event.data),
        },
      })
    })
  })

  // Cleanup on unmount
  onUnmounted(() => {
    stopLoopCheck()
    if (player.value && typeof player.value.destroy === 'function') {
      player.value.destroy()
      player.value = null
    }
  })

  // Expose methods
  defineExpose({
    play: () => player.value?.playVideo(),
    pause: () => player.value?.pauseVideo(),
    mute: () => player.value?.mute(),
    unMute: () => player.value?.unMute(),
    setVolume: (volume: number) => player.value?.setVolume(volume),
  })
</script>

<style scoped>
  .youtube-player {
    width: 100%;
    height: 100%;
  }

  .youtube-player :deep(iframe) {
    width: 100%;
    height: 100%;
  }
</style>
